import { CommonModule } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import { AfbApiModule } from '../afb-api/afb-api.module';
import { AfbGraphqlApiModule } from '../afb-graphql-api/afb-graphql-api.module';
import { GroupModule } from '../dashboard/group/group.module';
import { metaReducers, reducers } from '../reducers';
import { NamizePipe } from '../shared/pipes/namize.pipe';
import { SensorConditionPipe } from '../shared/pipes/sensor-condition.pipe';
import { SensorHeaderPipe } from '../shared/pipes/sensor-header.pipe';
import { SensorUnitPipe } from '../shared/pipes/sensor-unit.pipe';
import { TemperatureConverterPipe } from '../shared/pipes/temperature-converter.pipe';
import { SharedModule } from '../shared/shared.module';
import { ButtonModule, Dropdown2Module, DropdownModule, IconModule, TabsModule } from '../vision';
import { CardModule } from '../vision/card';
import { FormModule } from '../vision/form';
import { TableModule } from '../vision/table/table.module';
import { ToastModule } from '../vision/toast/toast.module';
import { AuthService, GoogleAnalyticsService, GroupService, OrgService, UserService, UtilService } from './';
import { AccessMgmtDialogComponent } from './components/access-mgmt-dialog/access-mgmt-dialog.component';
import { DeviceNameEditorDialogComponent } from './components/device-name-editor-dialog/device-name-editor-dialog.component';
import { DeviceSettingDialogComponent } from './components/device-setting-dialog/device-setting-dialog.component';
import { P13nDialogComponent } from './components/p13n-dialog/p13n-dialog.component';
import { HeaderComponent } from './containers/header/header.component';
import { CoreEffects } from './effects/core.effects';
import { AirDataEffects } from './store/effects/air-data.effect';
import { AvgAirDataEffects } from './store/effects/avg-air-data.effect';
import { GroupEffects } from './store/effects/group.effect';
import { TriggerEffects } from './store/effects/trigger.effect';
import { UserEffects } from './store/effects/user.effect';

/* Pipes to provide */
/* Effects */
@NgModule({
  imports: [
    CommonModule,
    BrowserAnimationsModule,
    FormsModule,
    // Importing HttpModule to provide http service app wide
    HttpClientModule,
    // ngRx
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot([
      CoreEffects,
      GroupEffects,
      UserEffects,
      AirDataEffects,
      AvgAirDataEffects,
      TriggerEffects
    ]),
    // API Module
    AfbApiModule.forRootWithState(),
    // Graphql API Module
    AfbGraphqlApiModule.forRootWithState(),
    SharedModule,
    // For P13nDialogComponent
    MatDialogModule,
    // Group
    GroupModule,
    TabsModule,
    DropdownModule,
    Dropdown2Module,
    ButtonModule,
    IconModule,
    FormModule,
    CardModule,
    TableModule,
    ToastModule
  ],
  exports: [HeaderComponent, P13nDialogComponent],
  declarations: [
    HeaderComponent,
    P13nDialogComponent,
    AccessMgmtDialogComponent,
    DeviceSettingDialogComponent,
    DeviceNameEditorDialogComponent
  ],
  entryComponents: [
    P13nDialogComponent,
    AccessMgmtDialogComponent,
    DeviceSettingDialogComponent,
    DeviceNameEditorDialogComponent
  ]
})
export class CoreModule {
  // Skips if CoreModule is being loaded in module other than AppModule
  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    if (parentModule) {
      throw new Error('CoreModule is already loaded');
    }
  }

  // Providing AuthService application wide
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: CoreModule,
      providers: [
        AuthService,
        OrgService,
        UserService,
        GroupService,
        UtilService,
        GoogleAnalyticsService,
        SensorHeaderPipe,
        SensorUnitPipe,
        SensorConditionPipe,
        TemperatureConverterPipe,
        NamizePipe
      ]
    };
  }
}
